import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  AlertTriangle, 
  Clock, 
  Download,
  RefreshCw,
  Trash2,
  CheckCircle2,
  XCircle,
  Activity,
  Server
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { formatInPrague } from '../utils/dateUtils';

const SystemLogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clearing, setClearing] = useState(false);

  const canView = hasPagePermission(user, 'admin', 'view');
  const canEdit = hasPagePermission(user, 'admin', 'edit');

  useEffect(() => {
    if (canView) {
      fetchStats();
      fetchLogs();
    }
  }, [canView]);

  useEffect(() => {
    fetchLogs();
  }, [activeTab, page]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/system-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const logType = activeTab === 'all' ? '' : activeTab;
      const response = await api.get(`/admin/system-logs?page=${page}&limit=50${logType ? `&log_type=${logType}` : ''}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Chyba při načítání logů');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Opravdu chcete smazat logy starší než 7 dní?')) return;
    
    try {
      setClearing(true);
      const response = await api.delete('/admin/system-logs/clear?older_than_days=7');
      toast.success(`Smazáno ${response.data.deleted_count} logů`);
      fetchStats();
      fetchLogs();
    } catch (error) {
      console.error('Error clearing logs:', error);
      toast.error('Chyba při mazání logů');
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (dateStr) => {
    return formatInPrague(dateStr, 'dd.MM.yyyy HH:mm:ss');
  };

  const getLogTypeIcon = (type) => {
    switch (type) {
      case 'slow_request':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'import':
        return <Download className="w-4 h-4 text-blue-500" />;
      default:
        return <Activity className="w-4 h-4 text-slate-500" />;
    }
  };

  const getLogTypeBadge = (type) => {
    switch (type) {
      case 'slow_request':
        return <Badge className="bg-orange-100 text-orange-700">Pomalý požadavek</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700">Chyba</Badge>;
      case 'import':
        return <Badge className="bg-blue-100 text-blue-700">Import</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Úspěch</Badge>;
    } else if (status === 'error') {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Chyba</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  if (!canView) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Přístup odepřen</h2>
          <p className="text-slate-600 mt-2">Nemáte oprávnění zobrazit systémové logy.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/users')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Server className="w-6 h-6" />
                Systémové logy
              </h1>
              <p className="text-slate-500 text-sm">Monitoring API požadavků, chyb a importů</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchStats(); fetchLogs(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Obnovit
            </Button>
            {canEdit && (
              <Button variant="outline" onClick={handleClearLogs} disabled={clearing} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4 mr-2" />
                {clearing ? 'Mažu...' : 'Smazat staré (7+ dní)'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pomalé požadavky (24h)</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.slow_requests_24h}</p>
                  </div>
                  <Clock className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Chyby (24h)</p>
                    <p className="text-2xl font-bold text-red-600">{stats.errors_24h}</p>
                  </div>
                  <XCircle className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Poslední import</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {stats.last_import ? formatDate(stats.last_import.created_at) : 'Žádný'}
                    </p>
                    {stats.last_import && (
                      <p className="text-xs text-slate-500">
                        {stats.last_import.status === 'success' ? 
                          `+${stats.last_import.records_added} přidáno, ${stats.last_import.records_updated} aktualizováno` :
                          stats.last_import.error || 'Chyba'
                        }
                      </p>
                    )}
                  </div>
                  <Download className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import History */}
        {stats?.import_logs?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5" />
                Historie importů z emailu
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Datum</th>
                      <th className="text-left p-2">Stav</th>
                      <th className="text-right p-2">Přidáno</th>
                      <th className="text-right p-2">Aktualizováno</th>
                      <th className="text-right p-2">Beze změn</th>
                      <th className="text-right p-2">Celkem</th>
                      <th className="text-left p-2">Chyba</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.import_logs.map((log, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-2">{formatDate(log.started_at)}</td>
                        <td className="p-2">{getStatusBadge(log.status)}</td>
                        <td className="p-2 text-right text-green-600 font-medium">+{log.records_added || 0}</td>
                        <td className="p-2 text-right text-blue-600 font-medium">{log.records_updated || 0}</td>
                        <td className="p-2 text-right text-slate-500">{log.records_unchanged || 0}</td>
                        <td className="p-2 text-right font-medium">{log.total_records || 0}</td>
                        <td className="p-2 text-red-600 text-xs max-w-xs truncate">{log.error_message || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
              <TabsList>
                <TabsTrigger value="all">Vše</TabsTrigger>
                <TabsTrigger value="slow_request" className="text-orange-600">
                  <Clock className="w-4 h-4 mr-1" />
                  Pomalé ({stats?.slow_requests_24h || 0})
                </TabsTrigger>
                <TabsTrigger value="error" className="text-red-600">
                  <XCircle className="w-4 h-4 mr-1" />
                  Chyby ({stats?.errors_24h || 0})
                </TabsTrigger>
                <TabsTrigger value="import" className="text-blue-600">
                  <Download className="w-4 h-4 mr-1" />
                  Importy
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Žádné logy k zobrazení</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3">Čas</th>
                        <th className="text-left p-3">Typ</th>
                        <th className="text-left p-3">Endpoint / Zpráva</th>
                        <th className="text-right p-3">Doba (ms)</th>
                        <th className="text-center p-3">Status</th>
                        <th className="text-left p-3">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 whitespace-nowrap text-slate-600">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {getLogTypeIcon(log.type)}
                              {getLogTypeBadge(log.type)}
                            </div>
                          </td>
                          <td className="p-3 font-mono text-xs">
                            {log.path || log.message || '-'}
                          </td>
                          <td className="p-3 text-right">
                            {log.duration_ms ? (
                              <span className={log.duration_ms > 2000 ? 'text-orange-600 font-medium' : ''}>
                                {log.duration_ms.toLocaleString()}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-center">
                            {log.status_code ? (
                              <Badge className={
                                log.status_code >= 500 ? 'bg-red-100 text-red-700' :
                                log.status_code >= 400 ? 'bg-orange-100 text-orange-700' :
                                'bg-green-100 text-green-700'
                              }>
                                {log.status_code}
                              </Badge>
                            ) : log.status ? (
                              getStatusBadge(log.status)
                            ) : '-'}
                          </td>
                          <td className="p-3 text-xs text-slate-500 max-w-xs truncate">
                            {log.error_message || log.error || 
                              (log.type === 'import' && log.records_added !== undefined ? 
                                `+${log.records_added} / ${log.records_updated} upd` : ''
                              ) || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Předchozí
                    </Button>
                    <span className="px-4 py-2 text-sm text-slate-600">
                      Strana {page} z {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Další
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SystemLogPage;
